// Piano voicing enumeration (#236) — candidates as PITCH SETS, the hand-span
// constraint where the guitar has a fret span. Pure module: no React.
//
// The canonical families, in pedagogical order:
//   close     four-way close: rotate the chord tones, stack ascending
//   drop-2    the app's namesake in pitch space: close, then the 2nd-from-top
//             voice dropped an octave — no strings required to say it. Spans
//             a 10th and up: a TWO-hand family (callers pass a wider span)
//   shell     Root + 3rd + 7th: compact inversions in one hand;
//             the wide R-7-10 spread is rotation 1, and the span rule decides
//             whether a hand covers it.
//   rootless  Bill Evans / Wynton Kelly: the 3-5-7-9 stack (dominants take
//             the 13 in place of the 5), no root — the bass player has it.
//             Rotation 0 is the A form, rotation 2 the B form; a voice-led
//             ii–V–I alternates them, which the pitch metric discovers on
//             its own (pinned by test).
//
// The chord catalogue meets this halfway: extension types (maj9, dom13,
// dom7♭9…) are ALREADY rootless stacks — "ninth chords drop the root" — so
// rootless voicings of those types are their own intervals, and altered
// ninths ride along for free.

import {
  CHORD_TYPES,
  chordOwnsFlatNine,
  isTriadType,
  triadOffsets,
  type ChordTypeId,
} from './chords';
import { spellVoicingMidis } from './chordSpelling';
import { pitchClassOf, sharpName } from './notes';
import { mantoothShapes } from './mantooth';
import { admittedTensions } from './tensions';
import type { Inversion } from './voicing';

export type PianoFamily =
  | 'close'
  | 'drop-2'
  | 'guide-tones'
  | 'guide-tones-root'
  | 'shell'
  | 'rootless'
  | 'two-hand'
  | 'upper-structure'
  | 'quartal'
  | 'mantooth';

export const PIANO_FAMILIES: readonly PianoFamily[] = [
  'close',
  'drop-2',
  'guide-tones',
  'guide-tones-root',
  'shell',
  'rootless',
  'two-hand',
  'upper-structure',
  'quartal',
  'mantooth',
];

export interface PianoVoicing {
  /** Sounding pitches, low→high. */
  midis: number[];
  /** Note names low→high, spelled diatonically against the chord's own root
   * (see chordSpelling.ts): an E♭7 reads Eb G Bb Db, never a C♯. */
  voicing: string[];
  inversion: Inversion;
  family: PianoFamily;
  /** Bottom-to-top reach in semitones — what ONE hand must cover (for the
   * two-hand family, the larger of the two hands' reaches). */
  span: number;
  /** Hand-splitting families only (two-hand, upper-structure, quartal,
   * guide-tones-root): which pitches each hand takes, both low→high. */
  hands?: { left: number[]; right: number[] };
  /** upper-structure only: which triad the right hand is playing, spelled
   * against the chord's own root (so "Ab / C7" reads Ab, never G#). */
  upperTriad?: { root: string; quality: 'major' | 'minor' };
}

export interface PianoEnumerateOptions {
  /** One hand's reach, bottom to top, in semitones (default 14, a major
   * 9th). Close stacks always fit; the shell's R-7-10 spread and the wider
   * drop-2 rotations are exactly where this bites. */
  maxHandSpan?: number;
  /** Register the whole voicing must sit in (inclusive MIDI bounds; default
   * C3–C6, the comping middle of the keyboard). */
  register?: { low: number; high: number };
  /** Apply the avoid-b9 rule (m2/m9-class between any two voices, dominant
   * ♭9 chords exempt) — the same rule the guitar grips obey. */
  avoidB9?: boolean;
  /** Reject voicings whose adjacent spacing sits below its low interval limit
   * (default true). The piano's own register rule: see LOW_INTERVAL_LIMIT. */
  lowIntervalLimit?: boolean;
  /** The root AS WRITTEN, used to spell the voicing diatonically (an E♭7's
   * ♭7 reads D♭, not C♯). Defaults to `rootName`, which for callers inside
   * the engine is its internal sharp name — pass the chart's display root to
   * keep a flat-key tune out of sharps. */
  displayRoot?: string;
}

const DEFAULT_SPAN = 14;
const DEFAULT_REGISTER = { low: 48, high: 84 }; // C3..C6

const mod12 = (n: number) => ((n % 12) + 12) % 12;

/** The avoid-b9 rule in pitch space — the TRUE minor-NINTH reading: a pair
 * of voices 13+ semitones apart reducing to a semitone class. An adjacent
 * minor SECOND is deliberately allowed, because it is a normal colour, not a
 * clash — the canonical dominant A form (3-13-♭7-9) puts the 13 a semitone
 * under the ♭7, and the rule must not eliminate the textbook voicing. The
 * guitar's `hasFlatNineAvoidInterval` now applies the same predicate (ruling
 * Aug 2026), so the two instruments agree on what the rule means. */
export function hasFlatNineClashMidis(midis: number[]): boolean {
  for (let i = 0; i < midis.length; i += 1) {
    for (let j = i + 1; j < midis.length; j += 1) {
      const interval = midis[j] - midis[i];
      if (interval >= 13 && interval % 12 === 1) return true;
    }
  }
  return false;
}

/** Rotate a pitch-class stack: the bottom `by` tones move to the top. */
function rotate(pcs: number[], by: number): number[] {
  const r = by % pcs.length;
  return [...pcs.slice(r), ...pcs.slice(0, r)];
}

/** Semitone offsets from the first pitch class, stacking each next class
 * strictly above the last. Steps are pitch class → pitch class; a repeated
 * class rises a full octave. */
function ascendingSteps(pcs: number[]): number[] {
  const out = [0];
  for (let i = 1; i < pcs.length; i += 1) {
    const step = mod12(pcs[i] - pcs[i - 1]);
    out.push(out[i - 1] + (step === 0 ? 12 : step));
  }
  return out;
}

/**
 * LOW INTERVAL LIMITS — the acoustic rule the guitar has no equivalent of,
 * and the one thing a fret-span model can never express. Close spacing turns
 * to mud in the bass register: the partials of two near notes collide, and
 * below a certain pitch a given interval stops reading as harmony and starts
 * reading as a rumble. Every arranging text carries a version of this table;
 * the numbers below are the standard convention (the LOWER note's lowest
 * acceptable pitch), taken conservatively where sources disagree.
 *
 * This is why the piano needed its own register thinking rather than a
 * transplanted span rule: a hand can easily reach C2–E2, and it should not.
 * Applied to ADJACENT voices, which is what spacing means. Intervals wider
 * than an octave are unconstrained — spreading is always safe.
 */
const LOW_INTERVAL_LIMIT: Record<number, number> = {
  1: 52, // m2  — E3
  2: 51, // M2  — Eb3
  3: 48, // m3  — C3
  4: 46, // M3  — Bb2
  5: 46, // P4  — Bb2
  6: 46, // TT  — Bb2
  7: 34, // P5  — Bb1
  8: 43, // m6  — G2
  9: 41, // M6  — F2
  10: 41, // m7 — F2
  11: 41, // M7 — F2
  // No entry for the octave or anything wider. The ensemble tables do limit
  // the octave, but that is about density in a scored texture; on a piano an
  // octave shares its partials exactly and the left-hand bass octave is a
  // standard device to the bottom of the instrument.
};

/** True when any adjacent pair sits below its low interval limit — i.e. the
 * voicing would sound muddy however comfortably the hand covers it. */
export function isMuddy(midis: number[]): boolean {
  for (let i = 1; i < midis.length; i += 1) {
    const interval = midis[i] - midis[i - 1];
    const limit = LOW_INTERVAL_LIMIT[interval];
    if (limit !== undefined && midis[i - 1] < limit) return true;
  }
  return false;
}

/** Stack pitch classes strictly ascending from a bass placement, sliding the
 * whole stack up the register octave-wise; span + register + b9 filtered. */
export function placeStack(
  order: number[],
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  family: PianoFamily,
  options: PianoEnumerateOptions,
  intervalsFromBass?: number[],
): PianoVoicing[] {
  const maxSpan = options.maxHandSpan ?? DEFAULT_SPAN;
  const register = options.register ?? DEFAULT_REGISTER;
  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const steps = intervalsFromBass ?? ascendingSteps(order);
  const out: PianoVoicing[] = [];
  let bottom = register.low + mod12(order[0] - register.low);
  for (; bottom <= register.high; bottom += 12) {
    const midis = steps.map((iv) => bottom + iv);
    const top = midis[midis.length - 1];
    if (top > register.high) break;
    const span = top - bottom;
    if (span > maxSpan) continue;
    if (avoidB9 && !chordOwnsFlatNine(chordType) && hasFlatNineClashMidis(midis)) continue;
    if (lowIntervalLimit && isMuddy(midis)) continue;
    out.push({
      midis,
      voicing: spellVoicingMidis(midis, options.displayRoot ?? rootName, chordType, sharpName),
      inversion,
      family,
      span,
    });
  }
  return out;
}

/** Close position: rotate the chord tones for the inversion, stack ascending. */
export function enumerateClosePiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const root = pitchClassOf(rootName);
  const pcs = CHORD_TYPES[chordType].intervals.map((iv) => mod12(root + iv));
  return placeStack(rotate(pcs, inversion), rootName, chordType, inversion, 'close', options);
}

/** Drop 2 in pitch space: the close stack with its 2nd-from-top voice down
 * an octave — exactly the operation the guitar family performs, minus the
 * strings. The bass changes, so intervals are given explicitly. */
export function enumerateDrop2Piano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const root = pitchClassOf(rootName);
  const pcs = rotate(
    CHORD_TYPES[chordType].intervals.map((iv) => mod12(root + iv)),
    inversion,
  );
  // Ascending close intervals from the close bass, then drop voice 2-from-top.
  // NB `ascendingSteps` measures pitch class → PITCH CLASS. Measuring each
  // step against the running INTERVAL instead is correct only when the bass
  // is C (where the two coincide numerically), which is exactly why a C-only
  // test could not see it; every other root came out with foreign pitch
  // classes and doubled voices.
  const close = ascendingSteps(pcs);
  // Each voice carries its pitch class WITH its offset, so re-sorting cannot
  // separate the two. Deriving the offsets from the sorted bottom while
  // assuming the dropped voice IS that bottom holds only when the close stack
  // fits in an octave — the already-rootless extension types (dom13 and
  // friends) reach past it, and there the two disagreed silently.
  const dropIndex = close.length - 2;
  const voices = close
    .map((offset, i) => ({ pc: pcs[i], offset: i === dropIndex ? offset - 12 : offset }))
    .sort((a, b) => a.offset - b.offset);
  const intervals = voices.map((v) => v.offset - voices[0].offset);
  return placeStack(
    voices.map((v) => v.pc),
    rootName,
    chordType,
    inversion,
    'drop-2',
    options,
    intervals,
  );
}

/** Drop the highest voice by octaves until the stack fits one hand, keeping
 * every pitch class. Ascending order is restored after each fold, so the
 * result is still a playable stack rather than a re-sorted pitch-class set. */
function foldIntoHand(offsets: number[], maxSpan = DEFAULT_SPAN): number[] {
  const folded = [...offsets].sort((a, b) => a - b);
  // Bounded rather than `while`: a degenerate stack must not spin here.
  for (let guard = 0; guard < offsets.length + 4; guard += 1) {
    if (folded[folded.length - 1] - folded[0] <= maxSpan) break;
    folded[folded.length - 1] -= 12;
    folded.sort((a, b) => a - b);
  }
  return folded;
}

/** The interval of the given classes actually present in a chord type, or
 * null — e.g. its 3rd-class is one of {sus2 2, min 3, maj 3, sus4 5}. */
export function intervalOf(chordType: ChordTypeId, classes: number[]): number | null {
  const set = new Set(CHORD_TYPES[chordType].intervals.map(mod12));
  for (const candidate of classes) if (set.has(candidate)) return candidate;
  return null;
}

/**
 * GUIDE TONES — the 3rd and 7th alone, the two tones that name the change;
 * the bass states the root. The first comping texture every method teaches,
 * and the one whose voice leading IS the lesson: a ii–V–I resolves each
 * chord's 7th a half step down onto the next chord's 3rd while the other
 * tone holds, which the nearest-voicing walk discovers on its own (pinned).
 *
 * Rotation 0 puts the 3rd on the bottom, rotation 1 the 7th — both orders
 * are the practice (the pair alternates as the line resolves); 2–3 are
 * empty. Extension types take their underlying seventh chord's guide tones,
 * same as the shell.
 */
export function enumerateGuideTonesPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  if (inversion !== 0 && inversion !== 1) return [];
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  if (third == null || seventh == null) return [];
  const root = pitchClassOf(rootName);
  const pair =
    inversion === 0
      ? [mod12(root + third), mod12(root + seventh)]
      : [mod12(root + seventh), mod12(root + third)];
  return placeStack(pair, rootName, chordType, inversion, 'guide-tones', options);
}

/**
 * GUIDE TONES + ROOT — the two-hand division of labour applied to the
 * simplest texture: the left hand states the root in its own register (the
 * two-hand family's E2–C4) under the guide-tone dyad. The duo version of
 * guide tones — with no bass player the root still sounds, and with one it
 * simply doubles the bassist, which is what a pianist's LH does anyway.
 * Distinct from the shell, which packs R-3-7 close under ONE hand: here the
 * root sits an octave-odd below and the dyad keeps its middle-register home,
 * so the guide-tone chain resolves identically while the LH walks roots.
 *
 * `inversion` is the dyad's (0 = 3rd low, 1 = 7th low; 2–3 empty).
 */
export function enumerateGuideTonesRootPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const dyads = enumerateGuideTonesPiano(rootName, chordType, inversion, options);
  if (!dyads.length) return [];
  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const root = pitchClassOf(rootName);
  const out: PianoVoicing[] = [];
  for (const dyad of dyads) {
    let bass = LH_REGISTER.low + mod12(root - LH_REGISTER.low);
    for (; bass <= LH_REGISTER.high; bass += 12) {
      if (bass >= dyad.midis[0]) continue;
      const midis = [bass, ...dyad.midis];
      if (avoidB9 && !chordOwnsFlatNine(chordType) && hasFlatNineClashMidis(midis)) continue;
      if (lowIntervalLimit && isMuddy(midis)) continue;
      out.push({
        midis,
        voicing: spellVoicingMidis(midis, options.displayRoot ?? rootName, chordType, sharpName),
        inversion,
        family: 'guide-tones-root',
        span: dyad.midis[1] - dyad.midis[0],
        hands: { left: [bass], right: dyad.midis },
      });
    }
  }
  return out;
}

/** Shell: root + 3rd + 7th, the two tones that NAME the quality. Rotation 0
 * is the one-hand R-3-7; rotation 1 the wide R-7-10 spread (span 15–16 —
 * admitted only when the hand allows). Rotations 2/3 put the 3rd/7th at
 * the bottom of a compact shell; the ensemble bass carries root motion.
 * The shell of an EXTENSION chord
 * (maj9, dom13, m7♭5♭9…) is the shell of its underlying seventh chord — the
 * extensions are exactly the colours a shell omits, and the root is stated
 * by name even where the catalogue's rootless stack drops it. (The old
 * "rootless types have no shell" refusal kept shells out of the comp picker
 * for half the vocabulary.) */
export function enumerateShellPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  if (third == null || seventh == null) return [];
  const root = pitchClassOf(rootName);
  if (inversion === 0) {
    // R-3-7 in one hand.
    return placeStack(
      [root, mod12(root + third), mod12(root + seventh)],
      rootName,
      chordType,
      inversion,
      'shell',
      options,
      [0, third, seventh],
    );
  }
  if (inversion === 1) {
    // R-7-10: the 3rd on top, a 10th above the root.
    return placeStack(
      [root, mod12(root + seventh), mod12(root + third)],
      rootName,
      chordType,
      inversion,
      'shell',
      options,
      [0, seventh, third + 12],
    );
  }
  return placeStack(
    rotate([root, mod12(root + third), mod12(root + seventh)], inversion - 1),
    rootName,
    chordType,
    inversion,
    'shell',
    options,
  );
}

/** The rootless stack (semitone offsets, ascending) for a chord type: the
 * catalogue's own intervals when the type is already rootless, else its
 * 3rd/5th/7th plus an added natural 9 — with the dominant convention that
 * the 13 replaces the 5. */
export function rootlessOffsets(chordType: ChordTypeId): number[] | null {
  const type = CHORD_TYPES[chordType];
  // The piano's dominant A form is 3-13-b7-9 (B form: b7-9-3-13).
  // The guitar catalogue's C13 = E-G-Bb-A is not that piano vocabulary.
  if (chordType === 'dom7' || chordType === 'dom9' || chordType === 'dom13') {
    return [4, 9, 10, 14];
  }
  // A diminished seventh is already a compact, complete sonority. Adding
  // a natural ninth by the generic minor recipe changes its character.
  if (chordType === 'dim7' || chordType === 'dimmaj7') return [...type.intervals];
  if (type.intervals[0] !== 0) {
    // maj9, dom13, dom7b9, min9… are already rootless stacks; ascend them.
    // A few reach past a hand as written — dom13 puts its 13 a 13th above the
    // 3rd — and an unplayable stack means the chord drops out of the comp
    // entirely. Folding the top voice down an octave keeps every pitch class
    // and invents nothing (C13 becomes E-G-A-B♭); the resulting adjacent
    // minor 2nd is a piano colour, which is exactly why our b9 rule reads
    // true minor NINTHS rather than the guitar's m2-inclusive version.
    return foldIntoHand([...type.intervals].sort((a, b) => a - b));
  }
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  const fifth = intervalOf(chordType, [7, 6, 8]);
  if (third == null || seventh == null || fifth == null) return null;
  // The dominant convention trades the fifth for the 13 — but only a PERFECT
  // fifth. An altered fifth (7♭5, 7♯5) is the chord's defining colour, and
  // trading it away voiced a 7♭5 with no ♭5 in it (audit Aug 2026).
  const middle = type.category === 'dominant' && fifth === 7 ? 9 : fifth;
  // HALF-DIMINISHED adds the 11, not the ♮9 (Stella audit, Aug 2026): the
  // Locrian ♮2 ninth is a deliberate modern colour, not a default — and on
  // the m7♭5-heavy tunes where the chord actually lives, the melody tends to
  // sit right beside it (Stella's head rubbed it a semitone in four bars).
  // The 11 is the conservative half-diminished tension every method reaches
  // for first.
  const added = third === 3 && fifth === 6 ? 17 : 14;
  return [third, middle, seventh, added].sort((a, b) => a - b);
}

/** LH register for the two-hand texture: low E of the bass clef's ledger
 * region up to middle C — where a comping left hand actually sits. */
export const LH_REGISTER = { low: 40, high: 60 }; // E2..C4

/**
 * The TWO-HAND comping texture — the first family that models the piano as
 * two hands with a division of labour rather than one stack under one span:
 * the LEFT hand states the root (adding the fifth below the stack when the
 * right hand doesn't already sound it — the dominant stacks trade theirs for
 * the 13), the RIGHT hand plays the rootless stack. Solo-piano comping in
 * one rule: C∆ comes out C3 + E-G-B-D, which IS the textbook Cmaj9 voicing.
 *
 * `inversion` is the RIGHT hand's rotation (A form / B form as in
 * `enumerateRootlessPiano`); the register option governs the right hand,
 * the left hand living in its own fixed register. Every combined stack
 * passes the same b9 + low-interval-limit gates as the one-hand families —
 * the mud rule is what keeps the root from carrying its fifth into the
 * cellar.
 */
export function enumerateTwoHandPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const stacks = enumerateRootlessPiano(rootName, chordType, inversion, options);
  if (!stacks.length) return [];
  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const root = pitchClassOf(rootName);
  const fifthPc = mod12(root + 7);
  const hasPerfectFifth = CHORD_TYPES[chordType].intervals.some((iv) => mod12(iv) === 7);
  const out: PianoVoicing[] = [];
  for (const stack of stacks) {
    const rhBottom = stack.midis[0];
    const rhPcs = new Set(stack.midis.map(mod12));
    const addFifth = hasPerfectFifth && !rhPcs.has(fifthPc);
    let bass = LH_REGISTER.low + mod12(root - LH_REGISTER.low);
    for (; bass <= LH_REGISTER.high; bass += 12) {
      const lefts = addFifth ? [[bass], [bass, bass + 7]] : [[bass]];
      for (const left of lefts) {
        if (left[left.length - 1] >= rhBottom) continue;
        const midis = [...left, ...stack.midis];
        if (avoidB9 && !chordOwnsFlatNine(chordType) && hasFlatNineClashMidis(midis)) {
          continue;
        }
        if (lowIntervalLimit && isMuddy(midis)) continue;
        out.push({
          midis,
          voicing: spellVoicingMidis(midis, options.displayRoot ?? rootName, chordType, sharpName),
          inversion,
          family: 'two-hand',
          span: Math.max(stack.span, left[left.length - 1] - left[0]),
          hands: { left, right: stack.midis },
        });
      }
    }
  }
  return out;
}

const TRIAD_QUALITIES: { quality: 'major' | 'minor'; intervals: [number, number, number] }[] = [
  { quality: 'major', intervals: [0, 4, 7] },
  { quality: 'minor', intervals: [0, 3, 7] },
];

/** The avoid-b9 rule for upper structures: same true-minor-ninth reading as
 * `hasFlatNineClashMidis`, except a pair whose UPPER note is an exempt pitch
 * class does not count — see the exemption reasoning at the call site. */
function hasUpperStructureClash(midis: number[], exemptPcs: Set<number>): boolean {
  for (let i = 0; i < midis.length; i += 1) {
    for (let j = i + 1; j < midis.length; j += 1) {
      const interval = midis[j] - midis[i];
      if (interval >= 13 && interval % 12 === 1 && !exemptPcs.has(mod12(midis[j]))) return true;
    }
  }
  return false;
}

/**
 * UPPER STRUCTURES (Levine, The Jazz Piano Book): a major or minor triad in
 * the right hand, every tone of which is a chord tone or an admitted tension
 * (see tensions.ts), over a left-hand shell — root+3rd+7th or root+7th. The
 * classic two-hand tension texture: D/C7 is 9-♯11-13, A♭/C7 is ♯9-♭13.
 *
 * The triads are DERIVED, never listed per type: all 24 root × quality
 * candidates are checked against (chord tones ∪ admitted tensions), and a
 * triad must add at least one TENSION — one that restates only chord tones is
 * an inversion of the chord, not an upper structure. The guide tones (the
 * 3rd- and 7th-class tones) must survive across the two hands, so the
 * root+7th shell is offered only when the triad itself carries the 3rd.
 *
 * `inversion` is the RH triad's inversion (0–2; a triad has no rotation 3).
 */
export function enumerateUpperStructurePiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  if (inversion === 3) return [];
  const maxSpan = options.maxHandSpan ?? DEFAULT_SPAN;
  const register = options.register ?? DEFAULT_REGISTER;
  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const type = CHORD_TYPES[chordType];
  const root = pitchClassOf(rootName);
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  if (third == null || seventh == null) return [];
  // The root is a chord tone even when a rootless stack drops it — the LH
  // shell exists to state it.
  const chordPcs = new Set([root, ...type.intervals.map((iv) => mod12(root + iv))]);
  const tensionPcs = new Set(admittedTensions(chordType).map((t) => mod12(root + t)));

  // THE ♭9 EXEMPTION, stated as a reason (the chordOwnsFlatNine discipline):
  // an upper structure exists to SOUND its tensions, and on a dominant the
  // admitted ♭9 sits a minor ninth above the root by construction — the shell
  // always states the root at the bottom, the triad carries the ♭9 an
  // octave-plus above. Filtering that pair would erase half the canonical
  // catalogue (F♯/C7 = ♭9 ♯11, A/C7 = Levine's 13♭9 structure). So on a
  // dominant whose vocabulary admits the ♭9, a minor ninth whose upper note
  // IS that ♭9 is the tension doing its job, not a clash. The ♯9 needs no
  // exemption: above the 3rd it reduces to a major 7th, and no triad can
  // carry both ♮9 and ♯9 to make the other pairing.
  const exemptPcs = new Set<number>();
  const flatNinePc = mod12(root + 1);
  if (type.category === 'dominant' && tensionPcs.has(flatNinePc)) exemptPcs.add(flatNinePc);

  const thirdPc = mod12(root + third);
  const shells: number[][] = [
    [0, third, seventh],
    [0, seventh],
  ];
  const out: PianoVoicing[] = [];
  for (const { quality, intervals } of TRIAD_QUALITIES) {
    for (let triadRoot = 0; triadRoot < 12; triadRoot += 1) {
      const pcs = intervals.map((iv) => mod12(triadRoot + iv));
      if (!pcs.every((pc) => chordPcs.has(pc) || tensionPcs.has(pc))) continue;
      if (!pcs.some((pc) => tensionPcs.has(pc))) continue;
      const rotated = rotate(pcs, inversion);
      const rhSteps = ascendingSteps(rotated);
      const rhSpan = rhSteps[rhSteps.length - 1];
      if (rhSpan > maxSpan) continue;
      for (const shellOffsets of shells) {
        // Guide tones across the hands: the two-note shell drops the 3rd, so
        // it serves only a triad that carries the 3rd itself.
        if (shellOffsets.length === 2 && !pcs.includes(thirdPc)) continue;
        if (shellOffsets[shellOffsets.length - 1] > maxSpan) continue;
        let bass = LH_REGISTER.low + mod12(root - LH_REGISTER.low);
        for (; bass <= LH_REGISTER.high; bass += 12) {
          const left = shellOffsets.map((offset) => bass + offset);
          const lhTop = left[left.length - 1];
          let rhBottom = register.low + mod12(rotated[0] - register.low);
          for (; rhBottom + rhSpan <= register.high; rhBottom += 12) {
            if (rhBottom <= lhTop) continue;
            const right = rhSteps.map((step) => rhBottom + step);
            const midis = [...left, ...right];
            if (
              avoidB9 &&
              !chordOwnsFlatNine(chordType) &&
              hasUpperStructureClash(midis, exemptPcs)
            ) {
              continue;
            }
            if (lowIntervalLimit && isMuddy(midis)) continue;
            const voicing = spellVoicingMidis(
              midis,
              options.displayRoot ?? rootName,
              chordType,
              sharpName,
            );
            // The triad's root is named through the SAME spelling the whole
            // voicing got, so "the ♭VI over B♭7" reads G♭, never F♯.
            const rootIndex = midis.findIndex((midi) => mod12(midi) === mod12(triadRoot));
            out.push({
              midis,
              voicing,
              inversion,
              family: 'upper-structure',
              span: Math.max(rhSpan, lhTop - left[0]),
              hands: { left, right },
              upperTriad: { root: voicing[rootIndex], quality },
            });
          }
        }
      }
    }
  }
  return out;
}

/** Generalized fourth stacks drawn from admitted tensions. This is broader
 * than Mantooth's named recipes and does not enforce his right-thumb register.
 * Use the separate Mantooth family for that method. */
export function enumerateQuartalPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  if (inversion !== 0) return [];
  const maxSpan = options.maxHandSpan ?? DEFAULT_SPAN;
  const register = options.register ?? DEFAULT_REGISTER;
  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const type = CHORD_TYPES[chordType];
  const root = pitchClassOf(rootName);
  const third = intervalOf(chordType, [4, 3, 5, 2]);
  const seventh = intervalOf(chordType, [10, 11, 9]);
  if (third == null || seventh == null) return [];
  const chordPcs = new Set([root, ...type.intervals.map((iv) => mod12(root + iv))]);
  const tensionPcs = new Set(admittedTensions(chordType).map((t) => mod12(root + t)));
  const available = new Set([...chordPcs, ...tensionPcs]);

  // Same ♭9 exemption as the upper structures, same reason: a dominant whose
  // vocabulary admits the ♭9 may stack it into the fourths, and the minor
  // ninth it forms is the tension doing its job.
  const exemptPcs = new Set<number>();
  const flatNinePc = mod12(root + 1);
  if (type.category === 'dominant' && tensionPcs.has(flatNinePc)) exemptPcs.add(flatNinePc);

  // Rival ninths never stack in ONE voicing — the same conservative ruling
  // tensions.ts states for dom7♯9 ("the altered scale does carry ♭9 beside
  // ♯9, but stacking both is contested"). The menu offers either; a fourths
  // stack sounding both also cannot spell: two seconds fight for one letter
  // beside a sounding 3rd (D♭ and D♯ against E).
  const sharpNinePc = mod12(root + 3);
  const stacksRivalNinths = (pcs: number[]) =>
    tensionPcs.has(flatNinePc) &&
    tensionPcs.has(sharpNinePc) &&
    pcs.includes(flatNinePc) &&
    pcs.includes(sharpNinePc);

  const out: PianoVoicing[] = [];
  const push = (left: number[], right: number[]) => {
    const midis = [...left, ...right];
    if (avoidB9 && !chordOwnsFlatNine(chordType) && hasUpperStructureClash(midis, exemptPcs)) {
      return;
    }
    if (lowIntervalLimit && isMuddy(midis)) return;
    out.push({
      midis,
      voicing: spellVoicingMidis(midis, options.displayRoot ?? rootName, chordType, sharpName),
      inversion: 0,
      family: 'quartal',
      span: Math.max(left[left.length - 1] - left[0], right[right.length - 1] - right[0]),
      hands: { left, right },
    });
  };

  if (mod12(seventh - third) === 6) {
    // The generic dominant: LH tritone, RH quartal trio. The book offers the
    // tritone BOTH ways up — "the tritone is reversed with the two different
    // generic dominant constructions": from the tonic the 3rd is the lowest
    // voice, from the fifth the 7th is.
    if (maxSpan < 10) return out; // the RH trio spans a minor 7th
    for (const lhBottomPc of [mod12(root + third), mod12(root + seventh)]) {
      for (let trioBottom = 0; trioBottom < 12; trioBottom += 1) {
        const trioPcs = [0, 5, 10].map((step) => mod12(trioBottom + step));
        if (!trioPcs.every((pc) => available.has(pc))) continue;
        if (stacksRivalNinths(trioPcs)) continue;
        let bass = LH_REGISTER.low + mod12(lhBottomPc - LH_REGISTER.low);
        for (; bass <= LH_REGISTER.high; bass += 12) {
          const left = [bass, bass + 6];
          let rhBottom = register.low + mod12(trioBottom - register.low);
          for (; rhBottom + 10 <= register.high; rhBottom += 12) {
            if (rhBottom <= left[1]) continue;
            push(left, [rhBottom, rhBottom + 5, rhBottom + 10]);
          }
        }
      }
    }
    return out;
  }

  // Pure chains: from each admitted top, descend in perfect fourths as far
  // as the admitted set carries (five voices at most — the book's size).
  const guides = [mod12(root + third), mod12(root + seventh)];
  for (const top of available) {
    const chain = [top];
    while (chain.length < 5) {
      const next = mod12(chain[chain.length - 1] - 5);
      if (!available.has(next) || chain.includes(next)) break;
      chain.push(next);
    }
    if (chain.length < 4) continue;
    const asc = [...chain].reverse();
    if (!asc.some((pc) => guides.includes(pc))) continue;
    if (stacksRivalNinths(asc)) continue;
    const steps = asc.map((_, index) => index * 5);
    const split = 2; // five voices: 2 LH / 3 RH (the Rule of Thumb); four: 2/2
    if (Math.max(steps[split - 1], steps[steps.length - 1] - steps[split]) > maxSpan) continue;
    let bottom = register.low + mod12(asc[0] - register.low);
    for (; bottom + steps[steps.length - 1] <= register.high; bottom += 12) {
      const midis = steps.map((step) => bottom + step);
      push(midis.slice(0, split), midis.slice(split));
    }
  }
  return out;
}

/** Mantooth's coupled hand layouts. Normal RH thumb: C4–C5, with the
 * lower two notes in the LH (ch. 2). Lower RH placements are admitted only
 * with a root as the lowest note, following pp. 12, 24–26; never by shifting
 * the hands independently. A fifth is our conservative maximum hand gap
 * (the book says avoid wide gaps, but gives no numerical cutoff). */
export function enumerateMantoothPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  if (inversion !== 0) return [];
  const root = pitchClassOf(rootName);
  const register = options.register ?? { low: 40, high: 84 };
  const maxSpan = options.maxHandSpan ?? DEFAULT_SPAN;
  const out: PianoVoicing[] = [];
  for (const shape of mantoothShapes(chordType)) {
    for (let tonic = 24 + root; tonic <= 96; tonic += 12) {
      const midis = shape.offsets.map((n) => tonic + n);
      const thumb = midis[shape.split];
      if (thumb < 54 || thumb > 72) continue;
      if (thumb < 60) {
        if (!shape.lowRoot) continue;
        // Closest root strictly below the second voice; no extra bass note.
        midis[0] = midis[1] - (mod12(midis[1] - root) || 12);
      }
      if (midis[0] < register.low || midis[midis.length - 1] > register.high) continue;
      const left = midis.slice(0, shape.split),
        right = midis.slice(shape.split);
      if (new Set(left.map(mod12)).size !== left.length) continue;
      const requiredAlteration =
        chordType === 'maj7b5' || chordType === 'dom7b5'
          ? 6
          : chordType === 'dom7s5'
            ? 8
            : chordType === 'dom7s9'
              ? 3
              : null;
      if (requiredAlteration !== null && !midis.some((m) => mod12(m - root) === requiredAlteration))
        continue;
      if (right[0] - left[left.length - 1] > 7) continue;
      const span = Math.max(left[left.length - 1] - left[0], right[right.length - 1] - right[0]);
      if (span > maxSpan) continue;
      if ((options.lowIntervalLimit ?? true) && isMuddy(midis)) continue;
      if (
        (options.avoidB9 ?? true) &&
        !chordOwnsFlatNine(chordType) &&
        hasFlatNineClashMidis(midis)
      )
        continue;
      const voicing = spellVoicingMidis(
        midis,
        options.displayRoot ?? rootName,
        chordType,
        sharpName,
      );
      out.push({ midis, voicing, inversion, family: 'mantooth', span, hands: { left, right } });
    }
  }
  return [...new Map(out.map((v) => [v.midis.join(','), v])).values()];
}

/** Rootless (Evans/Kelly): rotation 0 = the A form, rotation 2 = the B form.
 * The bass of the hand is a guide tone (3rd or 7th/6th), not an
 * arbitrary rotation beginning on an added tension. Diminished chords retain
 * their complete symmetric vocabulary. See docs/piano-voicing-review.md. */
export function enumerateRootlessPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const offsets = rootlessOffsets(chordType);
  if (!offsets) return [];
  const root = pitchClassOf(rootName);
  const pcs = rotate(
    offsets.map((iv) => mod12(root + iv)),
    inversion,
  );
  const bottom = mod12(pcs[0] - root);
  const guides = [intervalOf(chordType, [4, 3, 5, 2]), intervalOf(chordType, [10, 11, 9])];
  if (CHORD_TYPES[chordType].category !== 'diminished' && !guides.includes(bottom)) return [];
  const original = placeStack(pcs, rootName, chordType, inversion, 'rootless', options);
  if (chordType !== 'min7b5') return original;
  // The 11th makes the literal A stack wide and its B rotation unplayable.
  // Offer the same four tones folded above either guide-tone bass, as well
  // as any playable spread form. No natural ninth or altered-tone omission.
  const compact = [...pcs].sort((a, b) => mod12(a - pcs[0]) - mod12(b - pcs[0]));
  const folded = placeStack(compact, rootName, chordType, inversion, 'rootless', options);
  return [...new Map([...original, ...folded].map((v) => [v.midis.join(','), v])).values()];
}

/** A triad in the requested texture, without inventing a seventh. Guide
 * families use a third/fifth dyad (two orders); other families keep the full
 * triad (three inversions). Root-bearing textures add the LH root below it.
 * The catalogue's doubled octave is not repeated in the comping hand. */
export function enumerateTriadPiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  family: PianoFamily,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  const guidePair = family === 'guide-tones' || family === 'guide-tones-root';
  if (inversion > (guidePair ? 1 : 2)) return [];
  const root = pitchClassOf(rootName);
  // A triad has no seventh. Keep a two-voice texture using its 3rd and 5th,
  // with the root supplied by the bassist (or the explicit LH root).
  const offsets = triadOffsets(chordType);
  const pcs = (guidePair ? offsets.slice(1) : offsets).map((iv) => mod12(root + iv));
  const order = rotate(pcs, inversion);
  // Root-bearing textures keep their LH root below the upper voices.
  const twoHanded =
    family === 'two-hand' ||
    family === 'guide-tones-root' ||
    family === 'upper-structure' ||
    family === 'quartal';
  const stacks = placeStack(order, rootName, chordType, inversion, family, options);
  if (!twoHanded) return stacks;

  const avoidB9 = options.avoidB9 ?? true;
  const lowIntervalLimit = options.lowIntervalLimit ?? true;
  const out: PianoVoicing[] = [];
  for (const stack of stacks) {
    let bass = LH_REGISTER.low + mod12(root - LH_REGISTER.low);
    for (; bass <= LH_REGISTER.high; bass += 12) {
      if (bass >= stack.midis[0]) continue;
      const midis = [bass, ...stack.midis];
      if (avoidB9 && !chordOwnsFlatNine(chordType) && hasFlatNineClashMidis(midis)) continue;
      if (lowIntervalLimit && isMuddy(midis)) continue;
      out.push({
        ...stack,
        midis,
        voicing: spellVoicingMidis(midis, options.displayRoot ?? rootName, chordType, sharpName),
        hands: { left: [bass], right: stack.midis },
      });
    }
  }
  return out;
}

export function enumeratePiano(
  rootName: string,
  chordType: ChordTypeId,
  inversion: Inversion,
  family: PianoFamily,
  options: PianoEnumerateOptions = {},
): PianoVoicing[] {
  if (family === 'mantooth') return enumerateMantoothPiano(rootName, chordType, inversion, options);
  if (isTriadType(chordType)) {
    return enumerateTriadPiano(rootName, chordType, inversion, family, options);
  }
  if (family === 'close') return enumerateClosePiano(rootName, chordType, inversion, options);
  if (family === 'drop-2') return enumerateDrop2Piano(rootName, chordType, inversion, options);
  if (family === 'guide-tones') {
    return enumerateGuideTonesPiano(rootName, chordType, inversion, options);
  }
  if (family === 'guide-tones-root') {
    return enumerateGuideTonesRootPiano(rootName, chordType, inversion, options);
  }
  if (family === 'shell') return enumerateShellPiano(rootName, chordType, inversion, options);
  if (family === 'two-hand') return enumerateTwoHandPiano(rootName, chordType, inversion, options);
  if (family === 'upper-structure') {
    return enumerateUpperStructurePiano(rootName, chordType, inversion, options);
  }
  if (family === 'quartal') {
    return enumerateQuartalPiano(rootName, chordType, inversion, options);
  }
  return enumerateRootlessPiano(rootName, chordType, inversion, options);
}
